import React, {
  ComponentProps,
  CSSProperties,
  FC,
  memo,
  useContext,
  useEffect,
  useState,
} from 'react';
import * as t from 'io-ts';
import { FormProvider, useForm } from 'react-hook-form';
import mustache from 'mustache';
import { Column } from '@narvar/nth-grid';
import { useTheme, ThemeProvider } from '@narvar/nth-hook-theme';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import Button, { ButtonType } from '@narvar/nth-block-button';
import { RetailerTheme } from '@narvar/nth-hook-loma';
import { SemibigHeader } from '@narvar/nth-bit-header';
import Paragraph from '@narvar/nth-bit-paragraph';
import CustomComponentContext from '../CustomComponentContext';
import SelectList from '../form-fields/RegisteredSelectList';
import RegisteredPhoneInput from '../form-fields/RegisteredPhoneInput';
import TextLink from '../TextLink';
import {
  FormRow,
  FormColumn,
  ActionsRow,
  UpsLogoWrapper,
  UpsLogo,
} from '../form-layout';
import {
  useDeliveryInstructionsContext,
  UI_STATE,
  DEFAULT_DELIVERY_INSTRUCTIONS_CONTEXT,
} from '../../hooks/useDeliveryInstructionsState';
import submitDetails from '../../api/details';
import PanelHeaderText from '../PanelHeaderText';
import { SupportedInputType } from '../form-fields/BaseInput';
import RegisteredInput from '../form-fields/RegisteredInput';
import RegisteredCheckbox from '../form-fields/RegisteredCheckbox';
import MustacheRendering from './MustacheRendering';
import TemplateLink from './TemplateLink';
import StepHeader from '../StepHeader';
import AccordionTitle from './Accordion';
import { decode, optional } from '../../../server/lib/codec';

import {
  deliverInstructionsAuthorizeUpsMyChoice,
  deliverInstructionsAuthorizeUpsMyChoiceStatement,
} from './dummyText';
import DeliveryInstructionsSublimeOption, {
  sublimeOptionFieldValuesV,
} from '../DeliveryInstructionsSublimeOption';
import { useSublimeDeliveryInstructions } from '../../hooks/useSublimeDeliveryInstructions';

enum InputNames {
  leaveAtValue = 'leaveAtValue',
  phone = 'phone',
  email = 'email',
  confirmMyChoice = 'confirmMyChoice',
  allowDriverRelease = 'allowDriverRelease',
  sublimeOptin = 'sublimeOptin',
  sublimeEmail = 'sublimeEmail',
}

const FormDataV = t.intersection([
  t.strict(
    {
      [InputNames.leaveAtValue]: t.string,
      [InputNames.phone]: optional(t.string),
      [InputNames.email]: optional(t.string),
      [InputNames.confirmMyChoice]: optional(t.boolean),
      [InputNames.allowDriverRelease]: optional(t.boolean),
    },
    'FormDataV',
  ),
  sublimeOptionFieldValuesV,
]);

interface FormData extends t.TypeOf<typeof FormDataV> {}

interface DeliveryInstructionsProps {
  featureName?: string;
}

// TODO: these should eventually come from upstream carrier data
const UPS_TERMS_CONDITIONS_URL =
  'https://www.ups.com/assets/resources/media/en_US/GTC.pdf';
const UPS_WEBSITE_URL = 'https://www.ups.com';

const PrivacyLink: FC<{ children: string }> = ({ children }) => (
  <TemplateLink key="privacyLink" url={UPS_WEBSITE_URL}>
    {children}
  </TemplateLink>
);

const TermsLink: FC<{ children: string }> = ({ children }) => (
  <TemplateLink key="termsLink" url={UPS_TERMS_CONDITIONS_URL}>
    {children}
  </TemplateLink>
);

const DeliveryInstructions: FC<DeliveryInstructionsProps> = ({
  featureName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { state, track } = useContext(CustomComponentContext);
  const {
    state: {
      verifiedAddress,
      leaveAtOptions,
      leaveAtValue: defaultLeaveAtValue,
      registerAccount,
      asrSignatureRequired,
    },
    update: updateUIState,
  } = useDeliveryInstructionsContext();

  const { carrier, trackingNumbers, retailerMoniker, orderNumbers } =
    state?.pageContext || {};
  const trackingNumber = trackingNumbers?.[0];
  const orderNumber = orderNumbers?.[0];

  const { isEnabled } = useSublimeDeliveryInstructions();

  const methods = useForm<FormData>({
    defaultValues: { sublimeOptin: isEnabled },
  });

  const {
    register,
    reset,
    watch,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;

  useEffect(() => {
    setValue('sublimeOptin', isEnabled);
  }, [isEnabled, setValue]);

  const {
    deliveryInstructionsStep2Text,
    deliveryInstructionsDeliveryOptionsLabel,
    deliveryInstructionsLeaveInstructionsHeader,
    deliveryInstructionsLeaveInstructionsSubheader,
    deliveryInstructionsSubmitInstructionsButtonText,
    deliveryInstructionsCancelButtonText,
    deliveryInstructionsRequiredFieldErrorText,
    deliveryInstructionsProvidedByText,
    deliveryInstructionsPhoneNumber,
    deliveryInstructionsEmailAddress,
    deliveryInstructionsCheckboxDisclaimer,
  } = useTranslations();

  const {
    bodytextDefaultNone: bodyTextStyle,
    captiontextDefaultNone: captionTextStyle,
  } = useTheme() as {
    [key in keyof RetailerTheme]: CSSProperties;
  };

  const disclaimerTextTemplate = mustache.parse(
    deliveryInstructionsCheckboxDisclaimer || '',
  );

  const linkContext = new mustache.Context({
    privacyLink: () => PrivacyLink,
    termsLink: () => TermsLink,
  });

  // eslint-disable-next-line react/no-unstable-nested-components
  const UPSMyChoice = memo(({ children }) => (
    <AccordionTitle
      onClick={(e) => {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }}
    >
      {children}
    </AccordionTitle>
  ));
  const accordionText = mustache.parse(
    deliverInstructionsAuthorizeUpsMyChoice || '',
  );
  const accordionContext = new mustache.Context({
    upsMyChoice: () => UPSMyChoice,
  });

  // Reset the form when the component unmounts. There's no flow
  // to return back to the form with values remaining in the fields,
  // So once we're done with this panel, reset it right away.
  useEffect(() => reset, []);

  const logDeliveryInstructionsEvent = (opts: { success: boolean }) => {
    const successType = registerAccount
      ? 'submitAndRegisterAccountSuccess'
      : 'submitSuccess';
    const errorType = registerAccount
      ? 'submitAndRegisterAccountError'
      : 'submitError';

    track({
      eventType: 'click',
      clickSubType: opts.success ? successType : errorType,
    });

    if (asrSignatureRequired) {
      track({
        eventType: 'click',
        clickSubType: 'submitAndUpsAsrAuthroized',
      });
    }
  };

  // We type formData as unknown because react-hook-form does not enforce the types
  // at runtime. We tried to do something like useForm<Record<string, unknown>> but
  // this causes typing problems. Instead, we force the type to be unknown here and
  // then use a codec to ensure runtime type safety.
  const onSubmit = handleSubmit(async (formData: unknown) => {
    if (isSubmitting) return;

    const {
      leaveAtValue,
      email,
      phone,
      allowDriverRelease = false,
      sublimeEmail,
      sublimeOptin,
    } = decode(FormDataV, formData);

    if (!(carrier && trackingNumber && verifiedAddress && retailerMoniker))
      return;

    try {
      const {
        success = false,
        error,
        hasProfile = false,
      } = await submitDetails({
        addressVerification: verifiedAddress,
        leaveAtValue,
        carrier,
        trackingNumber,
        registerAccount,
        registerAccountDetails: registerAccount
          ? {
              email,
              phone,
            }
          : undefined,
        allowDriverRelease,
        retailerMoniker,
        sublimeEmail,
        orderNumber,
      });

      logDeliveryInstructionsEvent({
        success: success && error == null,
      });

      if (success) {
        const getSuccessUiState = () => {
          if (sublimeOptin) return UI_STATE.NARVAR_OPTIN_SUBMIT_SUCCESS;
          // if (narvarSubmitted) return UI_STATE.NARVAR_APPLIED_SUBMIT_SUCCESS:
          return UI_STATE.SUBMIT_SUCCESS;
        };

        updateUIState({
          deliveryInstructionsUIState: getSuccessUiState(),
          hasProfile,
        });
      }
    } catch {
      logDeliveryInstructionsEvent({
        success: false,
      });
    }
  });

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = () => {
    updateUIState({
      ...DEFAULT_DELIVERY_INSTRUCTIONS_CONTEXT,
      stepNavigationChange: true,
    });
  };

  return (
    <div style={{ padding: '0.75rem' }}>
      <StepHeader>
        <PanelHeaderText>{deliveryInstructionsStep2Text}</PanelHeaderText>
        <SemibigHeader>
          {deliveryInstructionsLeaveInstructionsHeader}
        </SemibigHeader>
      </StepHeader>
      <Paragraph
        containerCustomStyle={{ maxWidth: '33.75rem', margin: '1rem 0 2rem' }}
      >
        {deliveryInstructionsLeaveInstructionsSubheader}
      </Paragraph>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={onSubmit}
          noValidate
        >
          <FormRow>
            <FormColumn sm-columns={12}>
              <SelectList
                featureName={featureName}
                id={InputNames.leaveAtValue}
                label={deliveryInstructionsDeliveryOptionsLabel || ''}
                animateLabel
                errorMessage={
                  errors.leaveAtValue && errors.leaveAtValue.message
                }
                value={watch(InputNames.leaveAtValue)}
                register={register(InputNames.leaveAtValue, {
                  required: {
                    value: true,
                    message: deliveryInstructionsRequiredFieldErrorText || '',
                  },
                })}
              >
                {/*
                aria-label removes the following error message,
                but a screen reader doesn't actually read "State" for the option:
                "A control must be associated with a text label        jsx-a11y/control-has-associated-label"

                This doesn't seem to provide a diminished experience though. If we wanted to improve this though,
                we'd need to bring in something like reachUI/Listbox to have better control.
              */}
                <option
                  value={defaultLeaveAtValue || ''}
                  aria-label={deliveryInstructionsDeliveryOptionsLabel || ''}
                />
                {leaveAtOptions?.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </SelectList>
            </FormColumn>
          </FormRow>
          {registerAccount && (
            <>
              <FormRow>
                <FormColumn sm-columns={6}>
                  <RegisteredPhoneInput
                    featureName={featureName}
                    id={InputNames.phone}
                    label={deliveryInstructionsPhoneNumber || ''}
                    animateLabel
                    errorMessage={errors.phone && errors.phone.message}
                    value={watch(InputNames.phone)}
                    register={register(InputNames.phone, {
                      required: {
                        value: true,
                        message:
                          deliveryInstructionsRequiredFieldErrorText || '',
                      },
                    })}
                  />
                </FormColumn>
                <FormColumn sm-columns={6}>
                  <RegisteredInput
                    featureName={featureName}
                    id={InputNames.email}
                    label={deliveryInstructionsEmailAddress || ''}
                    type={SupportedInputType.email}
                    animateLabel
                    errorMessage={errors.email && errors.email.message}
                    value={watch(InputNames.email)}
                    register={register(InputNames.email, {
                      required: {
                        value: true,
                        message:
                          deliveryInstructionsRequiredFieldErrorText || '',
                      },
                    })}
                  />
                </FormColumn>
              </FormRow>
              <FormRow>
                <FormColumn xs-columns={12}>
                  <RegisteredCheckbox
                    featureName={featureName}
                    id={InputNames.confirmMyChoice}
                    errorMessage={
                      errors.confirmMyChoice && errors.confirmMyChoice.message
                    }
                    checked={watch(InputNames.confirmMyChoice)}
                    register={register(InputNames.confirmMyChoice, {
                      required: {
                        value: true,
                        message:
                          deliveryInstructionsRequiredFieldErrorText || '',
                      },
                    })}
                    labelBoxCellStyle={{ paddingTop: '0.125rem' }}
                  >
                    <div style={bodyTextStyle}>
                      <MustacheRendering
                        template={disclaimerTextTemplate}
                        context={linkContext}
                      />
                    </div>
                  </RegisteredCheckbox>
                </FormColumn>
              </FormRow>
            </>
          )}
          {asrSignatureRequired && (
            <>
              <FormRow>
                <FormColumn xs-columns={12}>
                  <RegisteredCheckbox
                    featureName={featureName}
                    id={InputNames.allowDriverRelease}
                    errorMessage={
                      errors.allowDriverRelease &&
                      errors.allowDriverRelease.message
                    }
                    checked={watch(InputNames.allowDriverRelease)}
                    register={register(InputNames.allowDriverRelease, {
                      required: {
                        value: true,
                        message:
                          deliveryInstructionsRequiredFieldErrorText || '',
                      },
                    })}
                    labelBoxCellStyle={{ paddingTop: '0.125rem' }}
                  >
                    <div style={bodyTextStyle}>
                      <MustacheRendering
                        template={accordionText}
                        context={accordionContext}
                      />
                    </div>
                  </RegisteredCheckbox>
                </FormColumn>
              </FormRow>
              {isOpen && (
                <FormRow>
                  <FormColumn xs-columns={12}>
                    <div style={captionTextStyle}>
                      <span>
                        {deliverInstructionsAuthorizeUpsMyChoiceStatement}
                      </span>
                    </div>
                  </FormColumn>
                </FormRow>
              )}
            </>
          )}
          <DeliveryInstructionsSublimeOption />
          <ActionsRow>
            <Column
              xs-style="display: block; text-align: center;"
              sm-style="display: none"
              xs-columns={12}
            >
              <Button big type={ButtonType.submit} disabled={isSubmitting}>
                {deliveryInstructionsSubmitInstructionsButtonText}
              </Button>
              <div style={{ marginTop: '1rem' }}>
                <TextLink onClick={handleCancel}>
                  {deliveryInstructionsCancelButtonText}
                </TextLink>
              </div>
            </Column>
            <FormColumn
              sm-columns={8}
              md-columns={7}
              xs-style="display: none"
              sm-style="display: block"
            >
              <Button type={ButtonType.submit} disabled={isSubmitting}>
                {deliveryInstructionsSubmitInstructionsButtonText}
              </Button>
              {/*
              TODO: This should probably use `nth-text-link`, but is not because:
              1) This will need `type` set to button so that it doesn't submit the form. Can't set type on `nth-text-link`
              2) This should probably have some padding on mobile to make it easier to tap. Can't set padding on `nth-text-link`
              3) `nth-text-link` doesn't have theming. It seems the text should be set to the primary button color
              */}
              <span style={{ marginLeft: '0.5rem' }}>
                <TextLink onClick={handleCancel}>
                  {deliveryInstructionsCancelButtonText}
                </TextLink>
              </span>
            </FormColumn>
            <UpsLogoWrapper sm-columns={4} md-columns={5}>
              <UpsLogo />
              <div
                // TODO: update this to bodytextEmphasisNone when it gets default style
                style={{
                  ...bodyTextStyle,
                  fontWeight: 'bold',
                }}
              >
                {deliveryInstructionsProvidedByText}
              </div>
            </UpsLogoWrapper>
          </ActionsRow>
        </form>
      </FormProvider>
    </div>
  );
};

const ThemedDeliveryInstructinos: FC<
  ComponentProps<typeof DeliveryInstructions>
> = (props) => {
  const { state } = useContext(CustomComponentContext);
  const featureName = state?.settings.featureName;
  const featureNameStr = typeof featureName === 'string' ? featureName : '';
  return (
    <ThemeProvider
      featureName={featureNameStr}
      themeTokens={[
        'bodytextDefaultNone',
        'captiontextDefaultNone',
        'linksTrackingDefault',
      ]}
    >
      <DeliveryInstructions
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </ThemeProvider>
  );
};

export default withTranslationsProvider(ThemedDeliveryInstructinos, {
  translationKeys: [
    'deliveryInstructionsStep2Text',
    'deliveryInstructionsDeliveryOptionsLabel',
    'deliveryInstructionsLeaveInstructionsHeader',
    'deliveryInstructionsLeaveInstructionsSubheader',
    'deliveryInstructionsSubmitInstructionsButtonText',
    'deliveryInstructionsCancelButtonText',
    'deliveryInstructionsRequiredFieldErrorText',
    'deliveryInstructionsProvidedByText',
    'deliveryInstructionsPhoneNumber',
    'deliveryInstructionsEmailAddress',
    'deliveryInstructionsCheckboxDisclaimer',
  ],
});
